import React from "react";
import "../../../AllComponentCSS.css";
import CodeCopyBlock from "../../../CodeCopyBlock";

const CSS_Counters = () => {
  return (
    <div className="tutorial-container">
      <h1 className="tutorial-title">🔢 CSS Counters</h1>

      <p className="tutorial-intro">
        <b>CSS Counters</b> are like variables maintained by CSS. They let you
        automatically number elements (headings, sections, lists) without
        writing numbers manually. You control them with{" "}
        <code>counter-reset</code>, <code>counter-increment</code>, and{" "}
        <code>content</code>.
      </p>

      {/* Example 1 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 1: Numbered Sections</h2>
        <p>Automatically add numbers before each heading using counters.</p>

        <CodeCopyBlock
          code={`/* CSS */
section {
  counter-reset: section;
}
section h2 {
  counter-increment: section;
}
section h2::before {
  content: "Section " counter(section) ": ";
  font-weight: bold;
  color: #2563eb;
}

/* HTML */
<section>
  <h2>Introduction</h2>
  <h2>CSS Basics</h2>
  <h2>Selectors</h2>
</section>`}
          language="css"
        />

        <section style={{ counterReset: "section", marginTop: "1rem" }}>
          <h2
            style={{
              counterIncrement: "section",
            }}
          >
            <span
              style={{
                fontWeight: "bold",
                color: "#2563eb",
                marginRight: "4px",
              }}
            >
              Section 1:
            </span>{" "}
            Introduction
          </h2>
          <h2 style={{ counterIncrement: "section" }}>
            <span
              style={{
                fontWeight: "bold",
                color: "#2563eb",
                marginRight: "4px",
              }}
            >
              Section 2:
            </span>{" "}
            CSS Basics
          </h2>
          <h2 style={{ counterIncrement: "section" }}>
            <span
              style={{
                fontWeight: "bold",
                color: "#2563eb",
                marginRight: "4px",
              }}
            >
              Section 3:
            </span>{" "}
            Selectors
          </h2>
        </section>
      </div>

      {/* Example 2 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 2: Nested Counters</h2>
        <p>
          You can create nested counters for chapters and sub-sections (like
          "1.1", "1.2").
        </p>

        <CodeCopyBlock
          code={`/* CSS */
body {
  counter-reset: chapter;
}
h2 {
  counter-reset: subsection;
  counter-increment: chapter;
}
h2::before {
  content: "Chapter " counter(chapter) ": ";
  font-weight: bold;
  color: #16a34a;
}
h3 {
  counter-increment: subsection;
}
h3::before {
  content: counter(chapter) "." counter(subsection) " ";
  color: #dc2626;
}

/* HTML */
<h2>Getting Started</h2>
<h3>Install</h3>
<h3>Setup</h3>
<h2>Advanced Topics</h2>
<h3>Selectors</h3>
<h3>Pseudo-classes</h3>`}
          language="css"
        />

        <div style={{ counterReset: "chapter", marginTop: "1rem" }}>
          <h2 style={{ counterReset: "subsection" }}>
            <span style={{ color: "#16a34a", fontWeight: "bold" }}>
              Chapter 1:
            </span>{" "}
            Getting Started
          </h2>
          <h3>
            <span style={{ color: "#dc2626", marginRight: "6px" }}>1.1</span>{" "}
            Install
          </h3>
          <h3>
            <span style={{ color: "#dc2626", marginRight: "6px" }}>1.2</span>{" "}
            Setup
          </h3>

          <h2 style={{ counterReset: "subsection" }}>
            <span style={{ color: "#16a34a", fontWeight: "bold" }}>
              Chapter 2:
            </span>{" "}
            Advanced Topics
          </h2>
          <h3>
            <span style={{ color: "#dc2626", marginRight: "6px" }}>2.1</span>{" "}
            Selectors
          </h3>
          <h3>
            <span style={{ color: "#dc2626", marginRight: "6px" }}>2.2</span>{" "}
            Pseudo-classes
          </h3>
        </div>
      </div>

      {/* Example 3 */}
      <div className="tutorial-section">
        <h2 className="section-title">👨‍💻 Example 3: Custom List Numbers</h2>
        <p>
          Replace default list numbers with custom CSS counters and styled
          bullets.
        </p>

        <CodeCopyBlock
          code={`/* CSS */
ol.custom-counter {
  list-style: none;
  counter-reset: mylist;
  padding-left: 0;
}
ol.custom-counter li {
  counter-increment: mylist;
  margin: 8px 0;
}
ol.custom-counter li::before {
  content: "Step " counter(mylist) ": ";
  font-weight: bold;
  color: #f97316;
}

/* HTML */
<ol class="custom-counter">
  <li>Download Files</li>
  <li>Extract ZIP</li>
  <li>Run Project</li>
</ol>`}
          language="css"
        />

        <ol
          className="custom-counter"
          style={{ listStyle: "none", counterReset: "mylist", paddingLeft: 0 }}
        >
          <li style={{ counterIncrement: "mylist", margin: "8px 0" }}>
            <span style={{ fontWeight: "bold", color: "#f97316" }}>
              Step 1:
            </span>{" "}
            Download Files
          </li>
          <li style={{ counterIncrement: "mylist", margin: "8px 0" }}>
            <span style={{ fontWeight: "bold", color: "#f97316" }}>
              Step 2:
            </span>{" "}
            Extract ZIP
          </li>
          <li style={{ counterIncrement: "mylist", margin: "8px 0" }}>
            <span style={{ fontWeight: "bold", color: "#f97316" }}>
              Step 3:
            </span>{" "}
            Run Project
          </li>
        </ol>
      </div>

      <div className="tutorial-section">
        <h2 className="section-title">🚀 Next Steps</h2>
        <p>
          CSS Counters are powerful for auto-numbering chapters, steps,
          questions, or any repeated elements. Try combining them with{" "}
          <code>::before</code> and <code>content</code> to design custom
          numbering systems.
        </p>
      </div>
    </div>
  );
};

export default CSS_Counters;
